import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ReactWordcloud from 'react-wordcloud';
import axios from "../../api/axiosInstance";
import { API_BASE_URL } from "../../config";

interface Chat {
    user_query: string;
    bot_response: string;
    created_at: string;
}
interface Pagination {
    page: number;
    limit: number;
    total_pages: number;
    total_chats: number;
}
interface AnalyticsData {
    total_chats: number;
    recent_chats: Chat[];
    top_keywords: [string, number][];
    busiest_hours: [number, number][];
    error_rate: number;
    pagination: Pagination;
}

const PER_PAGE = 5;
const COLORS = ["#10b981", "#ef4444"];

export default function ChatAnalyticsDashboard({ restaurantId }: { restaurantId: string }) {
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    if (!restaurantId)
    {
        restaurantId = localStorage.getItem("restaurant_id") || "";
    }

    useEffect(() => {
        const fetchAnalytics = async () => {
            const params: any = { page: currentPage, limit: PER_PAGE };
            if (startDate) params.start = startDate;
            if (endDate) params.end = endDate;

            const res = await axios.get<AnalyticsData>(`/analytic/analytics/${restaurantId}`, { params });
            setData(res.data);
        };

        fetchAnalytics();
    }, [restaurantId, startDate, endDate, currentPage]);

    if (!data) return <div className="p-6">Loading analytics...</div>;

    const pieData = [
        { name: "Success", value: 100 - data.error_rate },
        { name: "Errors", value: data.error_rate }
    ];

    return (
        <div className="p-6 space-y-8">
            <h1 className="text-3xl font-bold text-gray-800">📊 Chatbot Analytics Dashboard</h1>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
                <label className="flex flex-col">
                    <span className="font-medium text-sm">Start Date</span>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded p-2 text-sm" />
                </label>
                <label className="flex flex-col">
                    <span className="font-medium text-sm">End Date</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded p-2 text-sm" />
                </label>
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded shadow p-4 text-center">
                    <h3 className="text-sm text-gray-500">Total Chats</h3>
                    <p className="text-3xl font-bold text-blue-600">{data.total_chats}</p>
                </div>
                <div className="bg-white rounded shadow p-4 text-center">
                    <h3 className="text-sm text-gray-500">Error Rate</h3>
                    <p className="text-3xl font-bold text-red-500">{data.error_rate}%</p>
                </div>
                <div className="bg-white rounded shadow p-4 text-center">
                    <h3 className="text-sm text-gray-500">Top Keyword</h3>
                    <p className="text-xl">{data.top_keywords[0]?.[0] || "N/A"}</p>
                </div>
                <div className="bg-white rounded shadow p-4 text-center">
                    <h3 className="text-sm text-gray-500">Most Active Hour</h3>
                    <p className="text-xl">
                        {data.busiest_hours.length > 0
                            ? `${data.busiest_hours.reduce((max, curr) =>
                                curr[1] > max[1] ? curr : max
                            )[0].toString().padStart(2, '0')}:00`
                            : "--"}
                    </p>
                </div>

            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <div className="bg-white p-4 rounded shadow">
                    <h4 className="font-semibold mb-2">⏰ Busiest Chat Hours</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={data.busiest_hours
                                .sort((a, b) => a[0] - b[0])
                                .map(([hour, count]) => ({
                                    hour: `${hour.toString().padStart(2, '0')}:00`,
                                    count,
                                }))
                            }
                        >
                        <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3b82f6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-4 rounded shadow">
                    <h4 className="font-semibold mb-2">🧪 Error Breakdown</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" outerRadius={100}>
                                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-around text-sm mt-2">
                        <span className="text-green-600">✅ Success</span>
                        <span className="text-red-600">❌ Errors</span>
                    </div>
                </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 shadow-md p-6 rounded-lg">
                <h4 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">🌐 Keyword Trends</h4>
                <div className="h-[300px] w-full">
                    <ReactWordcloud
                        words={data.top_keywords.map(([text, value]) => ({ text, value }))}
                        options={{
                            colors: ["#3b82f6", "#6366f1", "#8b5cf6", "#ec4899", "#10b981"],
                            fontSizes: [14, 40],
                            fontFamily: "sans-serif",
                            enableTooltip: true,
                            deterministic: false,
                            rotations: 2,
                            rotationAngles: [-30, 30],
                            scale: "sqrt",
                        }}
                    />
                </div>
            </div>
            <div className="bg-white shadow p-4 rounded">
                <h4 className="text-lg font-bold mb-2">🧾 Chat Logs</h4>
                <p className="text-sm text-gray-600 mb-2">
                    View a summary above or download full chat logs for detailed analysis.
                </p>
                <a
                    href={`${API_BASE_URL}/analytic/download-logs/${restaurantId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-all"
                >
                    ⬇️ Download Chat Logs (CSV)
                </a>
            </div>
        </div>
    );
}
